import { Group, NumberInput, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { ModalFooter } from 'components/ModalFooter';
import { i18nKeys, useTranslation } from 'locales';
import type { LightProduct } from 'modules/Products/api';
import type { ProductVariables } from 'modules/Products/api/use-save-product';
import { isNullish } from 'remeda';
import { useGetConstants } from 'shared/utils/selectors';

type ProductFormValues = Omit<ProductVariables, 'priceHtva'> & {
  priceHtva: number | '';
  id?: string;
};

type ProductModalOptions = {
  onSubmit: (values: any) => void;
  initialValues?: Partial<LightProduct>;
};

export const ProductFormModal = ({ onSubmit, initialValues }: ProductModalOptions) => {
  const { t } = useTranslation();
  const constants = useGetConstants();

  const form = useForm<ProductFormValues>({
    initialValues: {
      name: initialValues?.name ?? '',
      description: initialValues?.description ?? '',
      priceHtva: initialValues?.priceHtva ?? '',
      taxCategoryCode: initialValues?.taxCategoryCode ?? '',
      id: initialValues?.id,
    },
    validate: {
      name: value => (!value ? t(i18nKeys.ERROR.FIELD_REQUIRED) : null),
      priceHtva: value =>
        value === '' || isNullish(value) ? t(i18nKeys.ERROR.FIELD_REQUIRED) : null,
      taxCategoryCode: value => (!value ? t(i18nKeys.ERROR.FIELD_REQUIRED) : null),
    },
  });

  const taxCategoryOptions = constants.tax_categories
    .filter((tc, index, arr) => arr.findIndex(x => x.value === tc.value) === index)
    .map(tc => ({ value: tc.value, label: tc.description }));

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack>
        <Group grow>
          <TextInput
            required
            label={t(i18nKeys.INVOICE.PRODUCT)}
            placeholder={t(i18nKeys.FORM.PRODUCT.PLACEHOLDER.NAME)}
            {...form.getInputProps('name')}
          />
          <NumberInput
            required
            label={t(i18nKeys.PRICE)}
            placeholder={t(i18nKeys.FORM.PRODUCT.PLACEHOLDER.PRICE)}
            decimalScale={2}
            {...form.getInputProps('priceHtva')}
          />
        </Group>
        <Select
          required
          label={t(i18nKeys.INVOICE.VAT_RATE)}
          placeholder={t(i18nKeys.FORM.PRODUCT.PLACEHOLDER.VAT_RATE)}
          data={taxCategoryOptions}
          searchable
          {...form.getInputProps('taxCategoryCode')}
        />
        <Textarea
          w={400}
          label={t(i18nKeys.DESCRIPTION)}
          placeholder={t(i18nKeys.FORM.PRODUCT.PLACEHOLDER.DESCRIPTION)}
          {...form.getInputProps('description')}
        />
        <ModalFooter onCancel={modals.closeAll} onSave={form.onSubmit(onSubmit)} />
      </Stack>
    </form>
  );
};
